import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import DashboardWrapper from "../../components/DashboardWrapper";
import Swal from "sweetalert2";
import authService from "../../services/authService";
import productService from "../../services/productService";

/* =========================================================
   MODULES
========================================================= */

const modules = [
  "Dashboard",
  "Sliders",
  "Brands",
  "Home Page Settings",
  "Advertise Management",
  "Products",
  "Orders",
  "Transactions",
  "Customer Management",
  "Contact Us",
  "Shipping Rules",
  "Settings",
];

/* =========================================================
   EMPTY PERMISSIONS
========================================================= */

const createEmptyPermissions = () => {
  const result = {};

  modules.forEach((moduleName) => {
    result[moduleName] = {
      view: false,
      create: false,
      update: false,
      delete: false,
    };
  });

  return result;
};

/* =========================================================
   GET CURRENT USER
========================================================= */

const getCurrentUserSafe = () => {
  try {
    return authService.getCurrentUser();
  } catch (error) {
    console.error(
      "Unable to get current user:",
      error
    );

    return null;
  }
};

/* =========================================================
   GET ROLE VALUES
========================================================= */

const getRoleValues = (user) => {
  if (!user) {
    return [];
  }

  if (typeof user === "string") {
    try {
      user = JSON.parse(user);
    } catch (error) {
      return [user];
    }
  }

  const values = [];

  const addValue = (value) => {
    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return;
    }

    if (
      typeof value === "string" ||
      typeof value === "number"
    ) {
      values.push(String(value));
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        addValue(item);
      });

      return;
    }

    if (typeof value === "object") {
      addValue(value.name);
      addValue(value.role);
      addValue(value.role_name);
      addValue(value.roleName);
      addValue(value.display_name);
      addValue(value.displayName);
      addValue(value.title);
      addValue(value.label);
      addValue(value.slug);
      addValue(value.key);
      addValue(value.code);
    }
  };

  /* ROOT */

  addValue(user.role);
  addValue(user.role_name);
  addValue(user.roleName);
  addValue(user.user_role);
  addValue(user.userRole);
  addValue(user.roles);
  addValue(user.userRoles);
  addValue(user.role_id);
  addValue(user.roleId);

  /* NESTED USER */

  addValue(user.user?.role);
  addValue(user.user?.role_name);
  addValue(user.user?.roleName);
  addValue(user.user?.roles);
  addValue(user.user?.userRoles);
  addValue(user.user?.role_id);
  addValue(user.user?.roleId);

  /* DATA */

  addValue(user.data?.role);
  addValue(user.data?.role_name);
  addValue(user.data?.roleName);
  addValue(user.data?.roles);
  addValue(user.data?.userRoles);
  addValue(user.data?.role_id);
  addValue(user.data?.roleId);

  /* DATA USER */

  addValue(user.data?.user?.role);
  addValue(user.data?.user?.role_name);
  addValue(user.data?.user?.roleName);
  addValue(user.data?.user?.roles);
  addValue(user.data?.user?.userRoles);
  addValue(user.data?.user?.role_id);
  addValue(user.data?.user?.roleId);

  return values;
};

/* =========================================================
   CHECK SUPER ADMIN
========================================================= */

const checkIsSuperAdmin = (user) => {
  const roles = getRoleValues(user);

  return roles.some((role) => {
    const normalized = String(role)
      .trim()
      .toLowerCase()
      .replace(/[\s_-]/g, "");

    return (
      normalized === "superadmin" ||
      normalized === "superadministrator"
    );
  });
};

/* =========================================================
   NORMALIZE TEXT
========================================================= */

const normalizeText = (value) => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, "");
};

/* =========================================================
   MODULE NAME MATCHING
========================================================= */

const findMatchingModule = (
  apiModuleName
) => {
  const apiNormalized =
    normalizeText(apiModuleName);

  /*
    API module names and frontend names
    may be slightly different.
  */

  const aliases = {
    dashboard: "Dashboard",

    sliders: "Sliders",

    brands: "Brands",

    homepage: "Home Page Settings",
    homepagesettings:
      "Home Page Settings",
    homesettings:
      "Home Page Settings",

    advertisemanagement:
      "Advertise Management",
    advertise:
      "Advertise Management",
    advertisements:
      "Advertise Management",

    products: "Products",

    orders: "Orders",

    transactions: "Transactions",

    customermanagement:
      "Customer Management",
    customers:
      "Customer Management",

    contactus: "Contact Us",

    shippingrules:
      "Shipping Rules",
    shippingrule:
      "Shipping Rules",

    settings: "Settings",
  };

  if (aliases[apiNormalized]) {
    return aliases[apiNormalized];
  }

  return modules.find(
    (moduleName) =>
      normalizeText(moduleName) ===
      apiNormalized
  );
};

/* =========================================================
   CONVERT API PERMISSIONS
========================================================= */

/*
API response:

{
  status: true,
  message: "...",
  data: {
    user_role_id: 4,
    role_name: "Order Manager",
    role_slug: "order-manager",
    modules: [
      {
        module_id: 3,
        module_name: "Orders",
        module_slug: "orders",
        permissions: [
          {
            permission_id: 12,
            name: "View",
            status: true
          },
          {
            permission_id: 13,
            name: "Create",
            status: true
          },
          {
            permission_id: 14,
            name: "Update",
            status: true
          },
          {
            permission_id: 15,
            name: "Delete",
            status: true
          }
        ]
      }
    ]
  }
}
*/

const convertApiPermissions = (
  apiData
) => {
  const result =
    createEmptyPermissions();

  if (
    !apiData ||
    !Array.isArray(apiData.modules)
  ) {
    return result;
  }

  apiData.modules.forEach(
    (apiModule) => {
      const frontendModule =
        findMatchingModule(
          apiModule.module_name
        );

      if (!frontendModule) {
        return;
      }

      if (
        !Array.isArray(
          apiModule.permissions
        )
      ) {
        return;
      }

      apiModule.permissions.forEach(
        (permission) => {
          const permissionName =
            normalizeText(
              permission.name
            );

          if (
            permissionName ===
            "view"
          ) {
            result[
              frontendModule
            ].view =
              Boolean(
                permission.status
              );
          }

          if (
            permissionName ===
            "create"
          ) {
            result[
              frontendModule
            ].create =
              Boolean(
                permission.status
              );
          }

          if (
            permissionName ===
            "update"
          ) {
            result[
              frontendModule
            ].update =
              Boolean(
                permission.status
              );
          }

          if (
            permissionName ===
            "delete"
          ) {
            result[
              frontendModule
            ].delete =
              Boolean(
                permission.status
              );
          }
        }
      );
    }
  );

  return result;
};

/* =========================================================
   SETTING RULE COMPONENT
========================================================= */

const SettingRule = () => {
  /* =======================================================
     USER
  ======================================================= */

  const user = useMemo(
    () => getCurrentUserSafe(),
    []
  );

  const isSuperAdmin = useMemo(
    () => checkIsSuperAdmin(user),
    [user]
  );

  /* =======================================================
     ROLES
  ======================================================= */

  const [roles, setRoles] =
    useState([]);

  const [rolesLoading, setRolesLoading] =
    useState(false);

  const [rolesError, setRolesError] =
    useState("");

  /* =======================================================
     SELECTED ROLE
  ======================================================= */

  const [selectedRole, setSelectedRole] =
    useState(null);

  /* =======================================================
     PERMISSIONS
  ======================================================= */

  const [permissions, setPermissions] =
    useState({});

  const [
    permissionsLoading,
    setPermissionsLoading,
  ] = useState(false);

  const [
    permissionsError,
    setPermissionsError,
  ] = useState("");

  /* =======================================================
     FETCH ROLES
  ======================================================= */

  useEffect(() => {
    const fetchRoles = async () => {
      if (!isSuperAdmin) {
        return;
      }

      setRolesLoading(true);
      setRolesError("");

      try {
        console.log(
          "Fetching Roles API..."
        );

        /*
          GET backend/roles
        */

        const response =
          await productService.getAllRoles();

        console.log(
          "Roles API Response:",
          response
        );

        /*
          apiService may return:

          {
            status: true,
            data: [...]
          }

          OR axios-style response:

          {
            data: {
              status: true,
              data: [...]
            }
          }
        */

        const result =
          response?.data &&
          !Array.isArray(
            response?.data
          ) &&
          Array.isArray(
            response?.data?.data
          )
            ? response.data
            : response;

        if (
          !result?.status
        ) {
          throw new Error(
            result?.message ||
              "Failed to load roles."
          );
        }

        if (
          !Array.isArray(
            result?.data
          )
        ) {
          throw new Error(
            "Invalid roles API response."
          );
        }

        const apiRoles =
          result.data.map(
            (role) => ({
              id: role.id,
              name:
                role.name ||
                `Role ${role.id}`,
              description:
                getRoleDescription(
                  role.name
                ),
            })
          );

        console.log(
          "Processed Roles:",
          apiRoles
        );

        setRoles(apiRoles);

        /*
          Automatically select first role
        */

        if (
          apiRoles.length > 0
        ) {
          setSelectedRole(
            (previous) => {
              if (
                previous &&
                apiRoles.some(
                  (role) =>
                    String(
                      role.id
                    ) ===
                    String(
                      previous
                    )
                )
              ) {
                return previous;
              }

              return apiRoles[0].id;
            }
          );
        } else {
          setSelectedRole(null);
        }
      } catch (error) {
        console.error(
          "Roles API Error:",
          error
        );

        setRolesError(
          error?.message ||
            "Unable to load roles."
        );

        setRoles([]);
        setSelectedRole(null);
      } finally {
        setRolesLoading(false);
      }
    };

    fetchRoles();
  }, [isSuperAdmin]);

  /* =======================================================
     ROLE DESCRIPTION
  ======================================================= */

  const getRoleDescription = (
    roleName
  ) => {
    const normalized =
      normalizeText(roleName);

    switch (normalized) {
      case "admin":
        return "Administrative access";

      case "productmanager":
        return "Manage products";

      case "ordermanager":
      case "ordersmanager":
        return "Manage orders";

      case "accountmanager":
      case "accountmanagers":
        return "Manage accounts and transactions";

      case "telecaller":
      case "telecallers":
        return "Customer support access";

      case "superadmin":
      case "superadministrator":
        return "Full system access";

      default:
        return "Role access";
    }
  };

  /* =======================================================
     SELECTED ROLE DETAILS
  ======================================================= */

  const selectedRoleDetails =
    roles.find(
      (role) =>
        String(role.id) ===
        String(selectedRole)
    );

  /* =======================================================
     FETCH ROLE PERMISSIONS
  ======================================================= */

  useEffect(() => {
    const fetchRolePermissions =
      async () => {
        if (
          !isSuperAdmin ||
          !selectedRole
        ) {
          return;
        }

        setPermissionsLoading(
          true
        );

        setPermissionsError("");

        /*
          Clear old role permissions
          while loading new role.
        */

        setPermissions((previous) => ({
          ...previous,
          [selectedRole]:
            createEmptyPermissions(),
        }));

        try {
          console.log(
            "Fetching Role Permissions..."
          );

          console.log(
            "Selected Role ID:",
            selectedRole
          );

          /*
            Dynamic API:

            backend/roles/4/permissions

            If selectedRole = 2:

            backend/roles/2/permissions
          */

          const response =
            await productService.getRolePermissions(
              selectedRole
            );

          console.log(
            "Role Permissions API Response:",
            response
          );

          /*
            Handle both possible apiService
            response structures.
          */

          const result =
            response?.data &&
            !Array.isArray(
              response?.data
            ) &&
            response?.data?.data
              ? response.data
              : response;

          if (
            !result?.status
          ) {
            throw new Error(
              result?.message ||
                "Failed to load role permissions."
            );
          }

          /*
            Actual API data:

            result.data = {
              user_role_id,
              role_name,
              role_slug,
              modules
            }
          */

          const apiPermissionData =
            result?.data;

          const convertedPermissions =
            convertApiPermissions(
              apiPermissionData
            );

          console.log(
            "Converted Permissions:",
            convertedPermissions
          );

          setPermissions(
            (previous) => ({
              ...previous,
              [selectedRole]:
                convertedPermissions,
            })
          );
        } catch (error) {
          console.error(
            "Role Permissions API Error:",
            error
          );

          setPermissionsError(
            error?.message ||
              "Unable to load role permissions."
          );

          setPermissions(
            (previous) => ({
              ...previous,
              [selectedRole]:
                createEmptyPermissions(),
            })
          );
        } finally {
          setPermissionsLoading(
            false
          );
        }
      };

    fetchRolePermissions();
  }, [
    selectedRole,
    isSuperAdmin,
  ]);

  /* =======================================================
     CURRENT PERMISSIONS
  ======================================================= */

  const currentPermissions =
    selectedRole
      ? permissions[selectedRole] ||
        createEmptyPermissions()
      : createEmptyPermissions();

  /* =======================================================
     PERMISSION CHANGE
  ======================================================= */

  const handlePermissionChange = (
    moduleName,
    accessType
  ) => {
    if (!selectedRole) {
      return;
    }

    setPermissions((previous) => ({
      ...previous,

      [selectedRole]: {
        ...(previous[
          selectedRole
        ] ||
          createEmptyPermissions()),

        [moduleName]: {
          ...(previous[
            selectedRole
          ]?.[moduleName] || {
            view: false,
            create: false,
            update: false,
            delete: false,
          }),

          [accessType]:
            !previous[
              selectedRole
            ]?.[moduleName]?.[
              accessType
            ],
        },
      },
    }));
  };

  /* =======================================================
     SELECT ALL
  ======================================================= */

  const handleSelectAll = (
    moduleName,
    checked
  ) => {
    if (!selectedRole) {
      return;
    }

    setPermissions((previous) => ({
      ...previous,

      [selectedRole]: {
        ...(previous[
          selectedRole
        ] ||
          createEmptyPermissions()),

        [moduleName]: {
          view: checked,
          create: checked,
          update: checked,
          delete: checked,
        },
      },
    }));
  };

  /* =======================================================
     ROLE CHANGE
  ======================================================= */

  const handleRoleChange = (
    roleId
  ) => {
    setSelectedRole(roleId);
    setPermissionsError("");
  };

  /* =======================================================
     SAVE
  ======================================================= */

  const handleSave = () => {
    if (!selectedRoleDetails) {
      Swal.fire({
        title: "Error",
        text: "Please select a role first.",
        icon: "error",
        confirmButtonText: "OK",
        customClass: {
          confirmButton:
            "btn btn-primary",
        },
        buttonsStyling: false,
      });

      return;
    }

    console.log(
      "================================"
    );

    console.log(
      "Selected Role ID:",
      selectedRoleDetails.id
    );

    console.log(
      "Selected Role:",
      selectedRoleDetails.name
    );

    console.log(
      "Current Permissions:",
      permissions[
        selectedRole
      ]
    );

    console.log(
      "================================"
    );

    /*
      IMPORTANT:

      You provided only GET API:

      GET /backend/roles/{id}/permissions

      No permission UPDATE API was provided.

      So currently this button only shows
      the selected permissions in console.

      Once you give me the UPDATE/SAVE API,
      I can connect this button to backend.
    */

    Swal.fire({
      title: "Permissions Loaded",
      text: "Permission changes are currently available in the UI. Update API is required to save them to the backend.",
      icon: "info",
      confirmButtonText: "OK",
      customClass: {
        confirmButton:
          "btn btn-primary",
      },
      buttonsStyling: false,
    });
  };

  /* =======================================================
     NOT SUPER ADMIN
  ======================================================= */

  if (!isSuperAdmin) {
    return (
      <DashboardWrapper>
        <div
          style={{
            padding: "24px",
            backgroundColor:
              "#f5f5f9",
            minHeight: "100vh",
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              padding: "30px",
              boxShadow:
                "0 2px 6px rgba(0,0,0,0.08)",
              textAlign: "center",
            }}
          >
            <i
              className="bx bx-lock-alt"
              style={{
                fontSize: "50px",
                color: "#FB6C22",
              }}
            />

            <h4
              style={{
                marginTop: "15px",
                color: "#32325d",
              }}
            >
              Access Denied
            </h4>

            <p
              style={{
                color: "#8592a3",
              }}
            >
              Only Super Admin can manage
              roles and permissions.
            </p>
          </div>
        </div>
      </DashboardWrapper>
    );
  }

  /* =======================================================
     UI
  ======================================================= */

  return (
    <DashboardWrapper>
      <div
        style={{
          padding: "24px",
          backgroundColor:
            "#f5f5f9",
          minHeight: "100vh",
        }}
      >
        {/* =================================================
            HEADER
        ================================================= */}

        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <div>
            <h4
              style={{
                margin: 0,
                fontSize: "22px",
                fontWeight: 600,
                color: "#32325d",
              }}
            >
              Role & Permissions
            </h4>

            <p
              style={{
                margin:
                  "5px 0 0",
                color: "#8592a3",
                fontSize: "14px",
              }}
            >
              Manage roles and module
              access permissions
            </p>
          </div>
        </div>

        {/* =================================================
            MAIN CONTENT
        ================================================= */}

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "260px minmax(0, 1fr)",
            gap: "20px",
            alignItems: "start",
          }}
        >
          {/* =================================================
              LEFT SIDE - ROLES
          ================================================= */}

          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              boxShadow:
                "0 2px 6px rgba(0,0,0,0.08)",
              overflow: "hidden",
            }}
          >
            {/* ROLE HEADER */}

            <div
              style={{
                padding: "18px",
                borderBottom:
                  "1px solid #e7e7e7",
              }}
            >
              <h6
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#32325d",
                }}
              >
                Roles
              </h6>

              <small
                style={{
                  color: "#8592a3",
                }}
              >
                Select a role
              </small>
            </div>

            {/* LOADING */}

            {rolesLoading && (
              <div
                style={{
                  padding:
                    "30px 15px",
                  textAlign:
                    "center",
                  color: "#8592a3",
                }}
              >
                <div
                  className="spinner-border"
                  role="status"
                  style={{
                    width: "28px",
                    height: "28px",
                    color:
                      "#FB6C22",
                  }}
                />

                <div
                  style={{
                    marginTop:
                      "10px",
                    fontSize:
                      "13px",
                  }}
                >
                  Loading roles...
                </div>
              </div>
            )}

            {/* ERROR */}

            {!rolesLoading &&
              rolesError && (
                <div
                  style={{
                    padding:
                      "20px 15px",
                    textAlign:
                      "center",
                  }}
                >
                  <i
                    className="bx bx-error-circle"
                    style={{
                      fontSize:
                        "35px",
                      color:
                        "#dc3545",
                    }}
                  />

                  <div
                    style={{
                      marginTop:
                        "8px",
                      fontSize:
                        "13px",
                      color:
                        "#dc3545",
                    }}
                  >
                    {rolesError}
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      window.location.reload()
                    }
                    style={{
                      marginTop:
                        "12px",
                      backgroundColor:
                        "#FB6C22",
                      border:
                        "none",
                      color:
                        "#fff",
                      padding:
                        "7px 14px",
                      borderRadius:
                        "5px",
                      cursor:
                        "pointer",
                    }}
                  >
                    Retry
                  </button>
                </div>
              )}

            {/* ROLES LIST */}

            {!rolesLoading &&
              !rolesError &&
              roles.length >
                0 && (
                <div
                  style={{
                    padding:
                      "8px",
                  }}
                >
                  {roles.map(
                    (role) => {
                      const isSelected =
                        String(
                          selectedRole
                        ) ===
                        String(
                          role.id
                        );

                      return (
                        <div
                          key={
                            role.id
                          }
                          onClick={() =>
                            handleRoleChange(
                              role.id
                            )
                          }
                          style={{
                            padding:
                              "13px 12px",
                            borderRadius:
                              "6px",
                            cursor:
                              "pointer",
                            marginBottom:
                              "4px",
                            backgroundColor:
                              isSelected
                                ? "rgba(251, 108, 34, 0.12)"
                                : "transparent",
                            borderLeft:
                              isSelected
                                ? "3px solid #FB6C22"
                                : "3px solid transparent",
                          }}
                        >
                          <div
                            style={{
                              display:
                                "flex",
                              alignItems:
                                "center",
                              gap: "10px",
                            }}
                          >
                            {/* ICON */}

                            <div
                              style={{
                                width:
                                  "34px",
                                height:
                                  "34px",
                                minWidth:
                                  "34px",
                                borderRadius:
                                  "50%",
                                backgroundColor:
                                  isSelected
                                    ? "#FB6C22"
                                    : "#eef0f2",
                                color:
                                  isSelected
                                    ? "#fff"
                                    : "#566a7f",
                                display:
                                  "flex",
                                alignItems:
                                  "center",
                                justifyContent:
                                  "center",
                              }}
                            >
                              <i className="bx bx-user" />
                            </div>

                            {/* NAME */}

                            <div>
                              <div
                                style={{
                                  fontSize:
                                    "14px",
                                  fontWeight:
                                    600,
                                  color:
                                    isSelected
                                      ? "#FB6C22"
                                      : "#566a7f",
                                }}
                              >
                                {
                                  role.name
                                }
                              </div>

                              <div
                                style={{
                                  fontSize:
                                    "11px",
                                  color:
                                    "#a1acb8",
                                  marginTop:
                                    "2px",
                                }}
                              >
                                {
                                  role.description
                                }
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              )}

            {/* NO ROLES */}

            {!rolesLoading &&
              !rolesError &&
              roles.length ===
                0 && (
                <div
                  style={{
                    padding:
                      "25px 15px",
                    textAlign:
                      "center",
                    color:
                      "#8592a3",
                    fontSize:
                      "13px",
                  }}
                >
                  No roles found.
                </div>
              )}
          </div>

          {/* =================================================
              RIGHT SIDE - PERMISSIONS
          ================================================= */}

          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              boxShadow:
                "0 2px 6px rgba(0,0,0,0.08)",
              overflow: "hidden",
              minWidth: 0,
            }}
          >
            {/* RIGHT HEADER */}

            <div
              style={{
                padding:
                  "18px 20px",
                borderBottom:
                  "1px solid #e7e7e7",
                display: "flex",
                justifyContent:
                  "space-between",
                alignItems:
                  "center",
                gap: "15px",
              }}
            >
              <div>
                <h6
                  style={{
                    margin: 0,
                    fontSize:
                      "17px",
                    fontWeight:
                      600,
                    color:
                      "#32325d",
                  }}
                >
                  {selectedRoleDetails
                    ?.name ||
                    "Select Role"}
                </h6>

                <small
                  style={{
                    color:
                      "#8592a3",
                  }}
                >
                  Manage module
                  permissions for
                  this role
                </small>
              </div>

              <span
                style={{
                  backgroundColor:
                    "rgba(251,108,34,0.12)",
                  color:
                    "#FB6C22",
                  padding:
                    "6px 12px",
                  borderRadius:
                    "20px",
                  fontSize:
                    "12px",
                  fontWeight:
                    600,
                  whiteSpace:
                    "nowrap",
                }}
              >
                {modules.length}{" "}
                Modules
              </span>
            </div>

            {/* =================================================
                PERMISSIONS LOADING
            ================================================= */}

            {permissionsLoading && (
              <div
                style={{
                  padding:
                    "12px 20px",
                  backgroundColor:
                    "#fff8f4",
                  color:
                    "#FB6C22",
                  fontSize:
                    "13px",
                  borderBottom:
                    "1px solid #eeeeee",
                  display:
                    "flex",
                  alignItems:
                    "center",
                  gap: "8px",
                }}
              >
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                />

                Loading permissions...
              </div>
            )}

            {/* =================================================
                PERMISSION ERROR
            ================================================= */}

            {permissionsError && (
              <div
                style={{
                  padding:
                    "12px 20px",
                  backgroundColor:
                    "#fff5f5",
                  color:
                    "#dc3545",
                  fontSize:
                    "13px",
                  borderBottom:
                    "1px solid #eeeeee",
                }}
              >
                <i className="bx bx-error-circle me-1" />

                {permissionsError}
              </div>
            )}

            {/* =================================================
                PERMISSION TABLE
            ================================================= */}

            <div
              style={{
                overflowX:
                  "auto",
              }}
            >
              <table
                style={{
                  width:
                    "100%",
                  borderCollapse:
                    "collapse",
                  minWidth:
                    "650px",
                }}
              >
                <thead>
                  <tr
                    style={{
                      backgroundColor:
                        "#f8f9fa",
                    }}
                  >
                    <th
                      style={{
                        textAlign:
                          "left",
                        padding:
                          "14px 20px",
                        fontSize:
                          "13px",
                        color:
                          "#566a7f",
                        fontWeight:
                          600,
                      }}
                    >
                      Module Name
                    </th>

                    <th
                      style={
                        headerStyle
                      }
                    >
                      View
                    </th>

                    <th
                      style={
                        headerStyle
                      }
                    >
                      Create
                    </th>

                    <th
                      style={
                        headerStyle
                      }
                    >
                      Update
                    </th>

                    <th
                      style={
                        headerStyle
                      }
                    >
                      Delete
                    </th>

                    <th
                      style={
                        headerStyle
                      }
                    >
                      All
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {modules.map(
                    (moduleName) => {
                      const modulePermissions =
                        currentPermissions[
                          moduleName
                        ] || {
                          view: false,
                          create: false,
                          update: false,
                          delete: false,
                        };

                      const allChecked =
                        modulePermissions.view &&
                        modulePermissions.create &&
                        modulePermissions.update &&
                        modulePermissions.delete;

                      return (
                        <tr
                          key={
                            moduleName
                          }
                          style={{
                            borderBottom:
                              "1px solid #eeeeee",
                          }}
                        >
                          {/* MODULE */}

                          <td
                            style={{
                              padding:
                                "14px 20px",
                              fontSize:
                                "14px",
                              fontWeight:
                                500,
                              color:
                                "#566a7f",
                            }}
                          >
                            <div
                              style={{
                                display:
                                  "flex",
                                alignItems:
                                  "center",
                                gap: "9px",
                              }}
                            >
                              <i
                                className="bx bx-layer"
                                style={{
                                  fontSize:
                                    "18px",
                                  color:
                                    "#FB6C22",
                                }}
                              />

                              {
                                moduleName
                              }
                            </div>
                          </td>

                          {/* VIEW */}

                          <PermissionCheckbox
                            checked={
                              modulePermissions.view
                            }
                            onChange={() =>
                              handlePermissionChange(
                                moduleName,
                                "view"
                              )
                            }
                          />

                          {/* CREATE */}

                          <PermissionCheckbox
                            checked={
                              modulePermissions.create
                            }
                            onChange={() =>
                              handlePermissionChange(
                                moduleName,
                                "create"
                              )
                            }
                          />

                          {/* UPDATE */}

                          <PermissionCheckbox
                            checked={
                              modulePermissions.update
                            }
                            onChange={() =>
                              handlePermissionChange(
                                moduleName,
                                "update"
                              )
                            }
                          />

                          {/* DELETE */}

                          <PermissionCheckbox
                            checked={
                              modulePermissions.delete
                            }
                            onChange={() =>
                              handlePermissionChange(
                                moduleName,
                                "delete"
                              )
                            }
                          />

                          {/* ALL */}

                          <td
                            style={{
                              textAlign:
                                "center",
                              padding:
                                "12px",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={
                                allChecked
                              }
                              onChange={(
                                event
                              ) =>
                                handleSelectAll(
                                  moduleName,
                                  event
                                    .target
                                    .checked
                                )
                              }
                              style={{
                                width:
                                  "17px",
                                height:
                                  "17px",
                                cursor:
                                  "pointer",
                                accentColor:
                                  "#FB6C22",
                              }}
                            />
                          </td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>
            </div>

            {/* =================================================
                FOOTER
            ================================================= */}

            <div
              style={{
                padding:
                  "15px 20px",
                borderTop:
                  "1px solid #eeeeee",
                display:
                  "flex",
                justifyContent:
                  "flex-end",
              }}
            >
              <button
                onClick={
                  handleSave
                }
                disabled={
                  !selectedRole ||
                  permissionsLoading
                }
                style={{
                  backgroundColor:
                    !selectedRole ||
                    permissionsLoading
                      ? "#ccc"
                      : "#FB6C22",
                  color:
                    "#fff",
                  border:
                    "none",
                  borderRadius:
                    "5px",
                  padding:
                    "9px 18px",
                  cursor:
                    !selectedRole ||
                    permissionsLoading
                      ? "not-allowed"
                      : "pointer",
                  fontSize:
                    "14px",
                  fontWeight:
                    500,
                }}
              >
                {permissionsLoading
                  ? "Loading..."
                  : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </DashboardWrapper>
  );
};

/* =========================================================
   CHECKBOX COMPONENT
========================================================= */

const PermissionCheckbox = ({
  checked,
  onChange,
}) => {
  return (
    <td
      style={{
        textAlign:
          "center",
        padding: "12px",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        style={{
          width: "17px",
          height: "17px",
          cursor:
            "pointer",
          accentColor:
            "#FB6C22",
        }}
      />
    </td>
  );
};

/* =========================================================
   TABLE HEADER STYLE
========================================================= */

const headerStyle = {
  textAlign: "center",
  padding: "14px 10px",
  fontSize: "13px",
  color: "#566a7f",
  fontWeight: 600,
};

export default SettingRule;